import { ReactNode, useMemo } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import SysTable from "app/class/sys-table";
import { useTables } from "app/hooks/useTables";
import { translateMetadataLabel } from "modules/record/shared/services/recordMetadataLabels";

interface TableNameAutocompleteInputProps {
    value?: string | null;
    onChange: (value: string | null) => void;
    disabled?: boolean;
    error?: boolean;
    helperText?: ReactNode;
    autoFocus?: boolean;
    fieldLabel?: string;
}

export function TableNameAutocompleteInput({
    value,
    onChange,
    disabled = false,
    error = false,
    helperText,
    autoFocus = false,
    fieldLabel,
}: TableNameAutocompleteInputProps) {
    const { t } = useTranslation();
    const { data: tables = [], isLoading } = useTables();
    const getTableLabel = (table: SysTable) => {
        if (!table?.name) return "";
        const translatedLabel = translateMetadataLabel(t, table);
        return translatedLabel ? `${translatedLabel} (${table.name})` : table.name;
    };
    const options = useMemo(
        () => [...tables].filter((table) => table.name).sort((left, right) => left.name.localeCompare(right.name)),
        [tables]
    );
    const selectedOption = useMemo(
        () => options.find((table) => table.name === value) ?? null,
        [options, value]
    );

    return (
        <Autocomplete
            openText={fieldLabel ? `${t("Open")} ${fieldLabel}` : undefined}
            clearText={fieldLabel ? `${t("Clear")} ${fieldLabel}` : undefined}
            options={options}
            loading={isLoading}
            value={selectedOption}
            disabled={disabled}
            onChange={(_, option) => onChange(option?.name ?? null)}
            getOptionLabel={getTableLabel}
            isOptionEqualToValue={(option, currentValue) => option.name === currentValue.name}
            renderInput={(params) => (
                <TextField
                    {...params}
                    autoFocus={autoFocus}
                    size="small"
                    fullWidth
                    error={error}
                    helperText={helperText}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {isLoading && <CircularProgress size={18} />}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
